import { Link } from 'react-router-dom';
import { LogOut, Monitor, Moon, Settings, Sun, UserRound } from 'lucide-react';
import { initials, USER_ROLE_LABELS, type SessionUser } from '@familienbaum/shared';
import { useLogout } from '@/lib/queries';
import { useTheme, type ThemePreference } from '@/lib/theme';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { Avatar } from '@/components/ui/misc';

/**
 * Konto, Einstellungen, Farbschema, Abmelden – hinter dem eigenen Gesicht.
 *
 * Steht in einer eigenen Datei, seit es zwei Plätze dafür gibt: breit in der
 * Kopfzeile, schmal auf der Startseite neben der Begrüßung und in der
 * „Mehr“-Schublade. Auf dem Telefon gibt es keine Kopfzeile mehr – ohne diese
 * beiden Wege wäre das Konto dort nicht erreichbar.
 */
export function UserMenu({ user }: { user: SessionUser }) {
  const logout = useLogout();
  const { preference, setPreference } = useTheme();

  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-sunken"
          aria-label="Kontomenü"
        >
          <Avatar alt={user.name} fallback={initials(user.name)} size="sm" />
          <span className="hidden min-w-0 pr-1 text-left lg:block">
            <span className="block truncate text-[13px] leading-4 font-medium text-ink">{user.name}</span>
            <span className="block truncate text-[11.5px] leading-4 text-ink-3">
              {USER_ROLE_LABELS[user.role]}
            </span>
          </span>
        </button>
      </MenuTrigger>

      <MenuContent className="min-w-56">
        <div className="px-2 py-1.5">
          <p className="truncate text-sm font-medium text-ink">{user.name}</p>
          <p className="truncate text-[12px] text-ink-3">{user.email}</p>
        </div>
        <MenuSeparator />

        <MenuItem asChild>
          <Link to="/einstellungen">
            <UserRound aria-hidden />
            Profil &amp; Passwort
          </Link>
        </MenuItem>
        {/* Stand vorher als Zahnrad in der Kopfzeile. Dort nahm es Platz weg,
            den im Entwurf die Hauptaktion der Seite hat – und geöffnet wird es
            selten. */}
        <MenuItem asChild>
          <Link to="/einstellungen">
            <Settings aria-hidden />
            Einstellungen
          </Link>
        </MenuItem>

        <MenuSeparator />
        <MenuLabel>Farbschema</MenuLabel>
        <MenuRadioGroup value={preference} onValueChange={(value) => setPreference(value as ThemePreference)}>
          <MenuRadioItem value="light">
            <Sun aria-hidden />
            Hell
          </MenuRadioItem>
          <MenuRadioItem value="dark">
            <Moon aria-hidden />
            Dunkel
          </MenuRadioItem>
          <MenuRadioItem value="system">
            <Monitor aria-hidden />
            System folgen
          </MenuRadioItem>
        </MenuRadioGroup>

        <MenuSeparator />
        <MenuItem danger onSelect={() => logout.mutate()}>
          <LogOut aria-hidden />
          Abmelden
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
